import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, X, Trash2, Plus, MessageSquare, Bot, User as UserIcon, Loader2 } from "lucide-react";

/**
 * Floating "Ask AI" chat bubble for the Executive Dashboard.
 * - Rendered only when the user has HO access (parent decides via prop).
 * - Multi-turn conversation persisted server-side.
 */
export function ExecAIAssistant() {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    loadSessions();
    api.get("/mfg/ai/suggested-prompts").then((r) => setSuggested(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, thinking]);

  const loadSessions = async () => {
    setLoadingList(true);
    try { const r = await api.get("/mfg/ai/sessions"); setSessions(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoadingList(false); }
  };

  const openSession = async (sid) => {
    try {
      const r = await api.get(`/mfg/ai/sessions/${sid}`);
      setSessionId(sid); setMessages(r.data.messages || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const newSession = async () => {
    try {
      const r = await api.post("/mfg/ai/sessions", { title: "New chat" });
      setSessionId(r.data.id); setMessages([]); loadSessions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this chat?")) return;
    try {
      await api.delete(`/mfg/ai/sessions/${sid}`);
      if (sid === sessionId) { setSessionId(null); setMessages([]); }
      loadSessions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    let sid = sessionId;
    if (!sid) {
      try {
        const r = await api.post("/mfg/ai/sessions", { title: q.slice(0, 60) });
        sid = r.data.id; setSessionId(sid); loadSessions();
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); return; }
    }
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: q }]);
    setInput(""); setThinking(true);
    try {
      const r = await api.post(`/mfg/ai/sessions/${sid}/ask`, { question: q });
      setMessages((m) => {
        // Replace tmp + append assistant
        const withoutTmp = m.filter((x) => !x.id.toString().startsWith("tmp-"));
        return [...withoutTmp, r.data.user_message, r.data.assistant_message];
      });
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reach AI");
      setMessages((m) => m.filter((x) => !x.id.toString().startsWith("tmp-")));
    } finally { setThinking(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="exec-ai-open"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#C05621] hover:bg-[#9C4221] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        title="Ask Executive AI"
      >
        <Sparkles className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-40 w-full max-w-md md:max-w-lg bg-white border border-[#EAE5D9] rounded-lg shadow-2xl flex flex-col" style={{ height: "min(80vh, 640px)" }} data-testid="exec-ai-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#EAE5D9] bg-gradient-to-r from-[#FEF9EA] to-white rounded-t-lg">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#C05621] text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
          <div>
            <div className="font-[Outfit] font-semibold text-sm">Executive AI</div>
            <div className="text-[10px] text-[#8B7355]">Powered by Claude Sonnet 4.5 · Live plant data</div>
          </div>
        </div>
        <button onClick={() => setOpen(false)} data-testid="exec-ai-close" className="p-1 rounded hover:bg-[#EAE5D9]"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sessions sidebar */}
        <div className="w-32 md:w-40 border-r border-[#EAE5D9] flex flex-col bg-[#FDFBF7]">
          <button onClick={newSession} className="px-2 py-2 mx-2 mt-2 rounded bg-[#2C1810] text-white text-xs flex items-center justify-center gap-1 hover:bg-[#5C4033]" data-testid="exec-ai-new">
            <Plus className="w-3 h-3" /> New
          </button>
          <div className="flex-1 overflow-y-auto px-1 pt-2 pb-2 space-y-1">
            {loadingList && <div className="text-center text-[10px] text-[#8B7355] py-4"><Loader2 className="w-3 h-3 animate-spin mx-auto" /></div>}
            {sessions.map((s) => (
              <div key={s.id} onClick={() => openSession(s.id)}
                className={`group px-2 py-1.5 rounded text-xs cursor-pointer flex items-start gap-1 ${s.id === sessionId ? "bg-[#C05621] text-white" : "hover:bg-[#EAE5D9]"}`}
                data-testid={`exec-ai-session-${s.id}`}>
                <MessageSquare className="w-3 h-3 shrink-0 mt-0.5" />
                <div className="flex-1 truncate leading-tight">{s.title || "Untitled"}</div>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }} className={`opacity-0 group-hover:opacity-100 ${s.id === sessionId ? "text-white/80" : "text-red-500"}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {!loadingList && sessions.length === 0 && <div className="text-center text-[10px] text-[#8B7355] py-4">No chats yet</div>}
          </div>
        </div>

        {/* Chat pane */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="exec-ai-messages">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Bot className="w-10 h-10 mx-auto text-[#C05621] mb-2" />
                <div className="font-[Outfit] text-lg font-semibold">Hi — I'm your Executive AI.</div>
                <div className="text-xs text-[#8B7355] mt-1">Ask me anything about today's factory performance, quality alerts, or wastage trends.</div>
                <div className="mt-4 space-y-1.5">
                  {suggested.map((p, i) => (
                    <button key={i} onClick={() => send(p)}
                      className="block w-full text-left px-3 py-2 bg-[#FEF9EA] border border-amber-200 rounded text-xs hover:bg-amber-100 transition"
                      data-testid={`exec-ai-suggested-${i}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`exec-ai-msg-${m.role}`}>
                {m.role === "assistant" && <div className="w-7 h-7 rounded-full bg-[#C05621] text-white shrink-0 flex items-center justify-center"><Bot className="w-3.5 h-3.5" /></div>}
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${m.role === "user" ? "bg-[#2C1810] text-white" : "bg-[#FDFBF7] border border-[#EAE5D9]"}`}>
                  <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                </div>
                {m.role === "user" && <div className="w-7 h-7 rounded-full bg-[#EAE5D9] text-[#5C4033] shrink-0 flex items-center justify-center"><UserIcon className="w-3.5 h-3.5" /></div>}
              </div>
            ))}
            {thinking && (
              <div className="flex gap-2 justify-start">
                <div className="w-7 h-7 rounded-full bg-[#C05621] text-white shrink-0 flex items-center justify-center"><Bot className="w-3.5 h-3.5" /></div>
                <div className="px-3 py-2 rounded-lg text-sm bg-[#FDFBF7] border border-[#EAE5D9] text-[#8B7355] flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#EAE5D9] bg-white rounded-br-lg">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask me anything about your bakery today…"
                disabled={thinking}
                data-testid="exec-ai-input"
              />
              <Button onClick={() => send()} disabled={thinking || !input.trim()} className="bg-[#C05621] text-white" data-testid="exec-ai-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-[10px] text-[#8B7355] mt-1.5 text-center">AI responses are based on live data — verify critical decisions with source reports.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
